/**
 * ResumeQuote — customer-facing entry point.
 *
 * Requires Quote ID + email as a second factor. Posts to
 * POST /api/orders/quote/{public_id}/resume. On success stores the
 * frozen quote payload in sessionStorage and redirects into
 * MissingStickers with a resume hint. If the backend reports the
 * quote is already converted, offers a link to the existing order.
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { track, EVENT } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResumeQuote() {
  const [quoteId, setQuoteId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [converted, setConverted] = useState(null);
  const [anon, setAnon] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!quoteId.trim() || !email.trim()) {
      toast.error("Quote ID and email are both required.");
      return;
    }
    setBusy(true); setConverted(null); setAnon(false);
    track(EVENT.QUOTE_RESUME_ATTEMPTED, { source_page: "resume-quote" });
    try {
      const r = await axios.post(
        `${API}/orders/quote/${encodeURIComponent(quoteId.trim())}/resume`,
        { email: email.trim() },
      );
      if (r.data?.already_converted) {
        setConverted(r.data);
        return;
      }
      // Anonymous age bucket so GA4 can measure quote-freshness funnel.
      const createdAt = r.data.created_at ? new Date(r.data.created_at) : null;
      const ageDays = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / 86400000) : null;
      const bucket = ageDays == null ? "unknown"
                   : ageDays === 0     ? "same_day"
                   : ageDays <= 3      ? "1_to_3_days"
                   : ageDays <= 7      ? "4_to_7_days"
                                       : "over_7_days";
      track(EVENT.QUOTE_RESUME_SUCCEEDED, {
        quote_age_bucket: bucket,
        source_page:      "resume-quote",
      });
      // Stash the frozen quote and jump into the ordering flow.
      sessionStorage.setItem("resumed_quote", JSON.stringify({
        quote_public_id: r.data.public_id,
        album_slug:      r.data.album_slug,
        items:           (r.data.parsed_snapshot || {}).items || [],
        created_at:      r.data.created_at,
      }));
      toast.success("Quote loaded — continue to checkout.");
      nav("/order-missing?resume=1");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 401) {
        // Neutral — do not leak whether the quote exists.
        if (detail && /anonymous|no.*email/i.test(String(detail))) {
          setAnon(true);
        } else {
          toast.error("We could not verify that quote. Check the Quote ID and email address and try again.");
        }
      } else if (status === 404) {
        // Same neutral message.
        toast.error("We could not verify that quote. Check the Quote ID and email address and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally { setBusy(false); }
  };

  if (converted) {
    return (
      <main className="max-w-lg mx-auto px-4 py-12" data-testid="resume-converted">
        <div className="border-2 border-emerald-800 bg-emerald-50 rounded-2xl p-6">
          <h1 className="font-display text-2xl">This quote has already been converted into an order.</h1>
          <div className="mt-3 text-sm">
            <div><span className="font-bold">Order:</span> <span className="font-mono" data-testid="resume-converted-order-id">{converted.order_public_id}</span></div>
            {converted.converted_at && <div className="text-[var(--smoke)]">Converted at {new Date(converted.converted_at).toLocaleString()}</div>}
          </div>
          <div className="mt-5 flex gap-2">
            <Link to={`/orders/${converted.order_public_id}`} className="btn-red text-sm no-underline" data-testid="resume-view-order">View Order</Link>
            <Link to="/order-missing" className="btn-ghost text-sm no-underline">Start a new quote</Link>
          </div>
        </div>
      </main>
    );
  }

  if (anon) {
    return (
      <main className="max-w-lg mx-auto px-4 py-12" data-testid="resume-anonymous">
        <div className="border-2 border-[var(--ink)] bg-white rounded-2xl p-6">
          <h1 className="font-display text-2xl">We can&apos;t resume this quote online.</h1>
          <p className="mt-2 text-sm text-[var(--smoke)]">
            This quote cannot be resumed online. Contact CollectorUp with your Quote ID for assistance.
          </p>
          <div className="mt-5 flex gap-2">
            <button onClick={() => setAnon(false)} className="btn-ghost text-sm" data-testid="resume-try-again">Try another quote</button>
            <Link to="/order-missing" className="btn-red text-sm no-underline">Start a new quote</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-md mx-auto px-4 py-12" data-testid="resume-quote-page">
      <h1 className="font-display text-3xl">Resume a Quote</h1>
      <p className="mt-2 text-sm text-[var(--smoke)]">
        Enter the Quote ID we sent you and the email address used to create it.
      </p>
      <form className="mt-6 space-y-4" onSubmit={submit}>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest font-bold">Quote ID</span>
          <input
            type="text"
            value={quoteId}
            onChange={(e) => setQuoteId(e.target.value)}
            placeholder="QT-2026-…"
            className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm font-mono"
            data-testid="resume-quote-id-input"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest font-bold">Email address</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full mt-1 border-2 border-[var(--ink)] rounded-lg px-3 py-2 text-sm"
            data-testid="resume-quote-email-input"
          />
        </label>
        <button type="submit" disabled={busy} className="btn-red text-sm w-full" data-testid="resume-quote-submit">
          {busy ? "Loading…" : "Load Quote"}
        </button>
        <div className="text-xs text-center text-[var(--smoke)]">
          <Link to="/order-missing" className="underline">Start a new quote instead</Link>
        </div>
      </form>
    </main>
  );
}
